import React, { useState } from 'react';
import { ClockIcon, CheckCircleIcon, XCircleIcon, MailIcon, UserIcon, Trash2Icon, SendIcon, PencilIcon } from 'lucide-react';
import { User, BrandRequest, Store } from '../data/types';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { BrandRequestModal } from '../components/admin/BrandRequestModal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { EmptyState } from '../components/ui/EmptyState';
import { PageTransition } from '../components/ui/PageTransition';
import { ApproveModal } from '../components/admin/ApproveModal';
import { brands } from '../data/brands';
interface AdminRequestsPageProps {
  user: User;
  requests: BrandRequest[];
  stores: Store[];
  onLogout: () => void;
  onBack: () => void;
  onActivateRequest: (requestId: string) => void;
  onSendRequest: (request: any, sendNow?: boolean) => void;
  onUpdateRequest: (requestId: string, request: any, sendNow?: boolean) => void;
  onApprove: (requestId: string, approverName: string, approverEmail: string, conditions?: string) => void;
  onReject: (requestId: string, approverName: string) => void;
  onRemoveRequest: (requestId: string) => void;
  activityLog?: any;
  brands?: any[];
}
export function AdminRequestsPage({
  user,
  requests,
  stores,
  onLogout,
  onBack,
  onActivateRequest,
  onSendRequest,
  onUpdateRequest,
  onApprove,
  onReject,
  onRemoveRequest,
  activityLog,
  brands
}: AdminRequestsPageProps) {
  const [editingRequest, setEditingRequest] = useState<BrandRequest | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [emailNotification, setEmailNotification] = useState<{
    brandName: string;
    brandEmail: string;
    action: 'approved' | 'rejected' | 'removed' | 'sent';
  } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    action: () => void;
  }>({
    isOpen: false,
    title: '',
    description: '',
    action: () => {}
  });
  const [approveModal, setApproveModal] = useState<{
    isOpen: boolean;
    request: BrandRequest | null;
  }>({
    isOpen: false,
    request: null
  });
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <SendIcon className="w-4 h-4 text-text-tertiary" data-id="element-1228" />;
      case 'pending':
        return <ClockIcon className="w-4 h-4 text-requested" data-id="element-1229" />;
      case 'brand-accepted':
        return <CheckCircleIcon className="w-4 h-4 text-amber-500" data-id="element-1230" />;
      case 'accepted':
        return <CheckCircleIcon className="w-4 h-4 text-available" data-id="element-1231" />;
      case 'accepted-with-conditions':
        return <CheckCircleIcon className="w-4 h-4 text-requested" data-id="element-1232" />;
      case 'rejected':
        return <XCircleIcon className="w-4 h-4 text-urgent" data-id="element-1233" />;
      default:
        return null;
    }
  };
  const getStatusBadgeVariant = (status: string): 'default' | 'high-traffic' | 'available' | 'internal' | 'blocked' => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'pending':
        return 'high-traffic';
      case 'brand-accepted':
        return 'high-traffic';
      case 'accepted':
        return 'available';
      case 'accepted-with-conditions':
        return 'internal';
      case 'rejected':
        return 'blocked';
      default:
        return 'default';
    }
  };
  const getStatusLabel = (status: string, requestedBy?: string) => {
    switch (status) {
      case 'draft':
        return 'Pending Sending';
      case 'pending':
        return requestedBy === 'woolworths' ? 'Awaiting Brand Response' : 'Awaiting Your Approval';
      case 'brand-accepted':
        return 'Brand Confirmed — Needs Your Approval';
      case 'accepted':
        return 'Approved';
      case 'accepted-with-conditions':
        return 'Approved with Conditions';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  const getBrandEmail = (brandId: string) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.contactEmail || 'unknown@brand.co.za';
  };
  const handleApprove = (request: BrandRequest) => {
    setApproveModal({
      isOpen: true,
      request
    });
  };
  const handleApproveAsIs = (request: BrandRequest) => {
    const brandEmail = getBrandEmail(request.brandId);
    onApprove(request.id, user.name, user.email);
    setEmailNotification({
      brandName: request.brandName,
      brandEmail,
      action: 'approved'
    });
    setTimeout(() => setEmailNotification(null), 5000);
  };
  const handleApproveWithConditions = (request: BrandRequest, conditions: string) => {
    const brandEmail = getBrandEmail(request.brandId);
    onApprove(request.id, user.name, user.email, conditions);
    setEmailNotification({
      brandName: request.brandName,
      brandEmail,
      action: 'approved'
    });
    setTimeout(() => setEmailNotification(null), 5000);
    setApproveModal({
      isOpen: false,
      request: null
    });
  };
  const handleReject = (request: BrandRequest) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reject Booking Request',
      description: `Are you sure you want to reject the booking request from ${request.brandName}? This will notify the brand.`,
      action: () => {
        const brandEmail = getBrandEmail(request.brandId);
        onReject(request.id, user.name);
        setEmailNotification({
          brandName: request.brandName,
          brandEmail,
          action: 'rejected'
        });
        setTimeout(() => setEmailNotification(null), 5000);
        setConfirmDialog(prev => ({
          ...prev,
          isOpen: false
        }));
      }
    });
  };
  const handleRemove = (request: BrandRequest) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Request',
      description: `Are you sure you want to permanently delete this request for ${request.brandName}? This action cannot be undone.`,
      action: () => {
        onRemoveRequest(request.id);
        setEmailNotification({
          brandName: request.brandName,
          brandEmail: getBrandEmail(request.brandId),
          action: 'removed'
        });
        setTimeout(() => setEmailNotification(null), 4000);
        setConfirmDialog(prev => ({
          ...prev,
          isOpen: false
        }));
      }
    });
  };
  const handleSendToBrand = (request: BrandRequest) => {
    onActivateRequest(request.id);
    setEmailNotification({
      brandName: request.brandName,
      brandEmail: getBrandEmail(request.brandId),
      action: 'sent'
    });
    setTimeout(() => setEmailNotification(null), 5000);
  };
  const draftCount = requests.filter(r => r.status === 'draft').length;
  const pendingCount = requests.filter(r => r.status === 'pending' || r.status === 'brand-accepted').length;
  const acceptedCount = requests.filter(r => r.status === 'accepted' || r.status === 'accepted-with-conditions').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-1234">
      <Header user={user} onLogout={onLogout} currentView="admin-requests" onNavigate={onBack} draftCount={draftCount} stores={stores} brands={brands} brandRequests={requests} activityEntries={activityLog?.entries} unreadCount={activityLog?.unreadCount} onMarkAllRead={activityLog?.markAllAsRead} onMarkRead={activityLog?.markAsRead} data-id="element-1235" />

      <PageTransition className="flex-1 flex flex-col w-full" data-id="element-1236">
        <main className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-6 py-8" data-id="element-1237">
          <div className="flex items-center justify-between mb-6" data-id="element-1238">
            <div data-id="element-1239">
              <h1 className="text-2xl font-bold text-text-primary mb-1" data-id="element-1240">
                Booking Requests
              </h1>
              <p className="text-sm text-text-secondary" data-id="element-1241">
                All media booking requests — from Woolworths to brands and from
                brands to Woolworths.
              </p>
            </div>
          </div>

          {/* Status Summary */}
          <div className="flex items-center gap-4 mb-6" data-id="element-1242">
            <div className="flex items-center gap-2 px-3 py-2 bg-surface-warm border border-border rounded-lg" data-id="element-1243">
              <SendIcon className="w-4 h-4 text-text-tertiary" data-id="element-1244" />
              <span className="text-sm font-medium text-text-primary" data-id="element-1245">
                {draftCount}
              </span>
              <span className="text-xs text-text-secondary" data-id="element-1246">
                Pending Sending
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-requested-bg border border-requested/20 rounded-lg" data-id="element-1247">
              <ClockIcon className="w-4 h-4 text-requested" data-id="element-1248" />
              <span className="text-sm font-medium text-text-primary" data-id="element-1249">
                {pendingCount}
              </span>
              <span className="text-xs text-text-secondary" data-id="element-1250">
                Awaiting Response
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-available-bg border border-available/20 rounded-lg" data-id="element-1251">
              <CheckCircleIcon className="w-4 h-4 text-available" data-id="element-1252" />
              <span className="text-sm font-medium text-text-primary" data-id="element-1253">
                {acceptedCount}
              </span>
              <span className="text-xs text-text-secondary" data-id="element-1254">Approved</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-urgent/5 border border-urgent/20 rounded-lg" data-id="element-1255">
              <XCircleIcon className="w-4 h-4 text-urgent" data-id="element-1256" />
              <span className="text-sm font-medium text-text-primary" data-id="element-1257">
                {rejectedCount}
              </span>
              <span className="text-xs text-text-secondary" data-id="element-1258">Rejected</span>
            </div>
            <div className="ml-auto text-xs text-text-tertiary" data-id="element-1259">
              {requests.length} total request{requests.length !== 1 ? 's' : ''}
            </div>
          </div>

          {requests.length === 0 ? <EmptyState icon={MailIcon} title="No booking requests" description="There are currently no booking requests to review or drafts to send." data-id="element-1260" /> : <div className="bg-white border border-border rounded-lg overflow-hidden shadow-sm" data-id="element-1261">
              <table className="w-full text-left border-collapse" data-id="element-1262">
                <thead data-id="element-1263">
                  <tr className="bg-surface-warm border-b border-border" data-id="element-1264">
                    <th className="px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1265">
                      Brand
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1266">
                      Store & Media
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1267">
                      Period
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1268">
                      Date
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1269">
                      Requested By
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1270">
                      Status
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1271">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border" data-id="element-1272">
                  {requests.length === 0 ? <tr data-id="element-1273">
                      <td colSpan={7} className="px-5 py-12 text-center text-text-secondary" data-id="element-1274">
                        No requests yet.
                      </td>
                    </tr> : requests.map(request => {
                const isWoolworths = request.requestedBy === 'woolworths';
                return <tr key={request.id} className="hover:bg-surface transition-colors" data-id="element-1275">
                          {/* Brand */}
                          <td className="px-5 py-4" data-id="element-1276">
                            <div className="font-medium text-text-primary" data-id="element-1277">
                              {request.brandName}
                            </div>
                            {request.contactName ? <div className="text-xs text-text-secondary mt-0.5" data-id="element-1278">
                                {request.contactName}
                                {request.contactJobTitle && <span className="text-text-tertiary" data-id="element-1279">
                                    {' '}
                                    · {request.contactJobTitle}
                                  </span>}
                              </div> : <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1280">
                                ID: {request.id}
                              </div>}
                          </td>

                          {/* Store & Media */}
                          <td className="px-5 py-4" data-id="element-1281">
                            <div className="text-sm text-text-primary" data-id="element-1282">
                              {request.stores.length === 1 ? request.stores[0]?.storeName : `${request.stores.length} Stores`}
                            </div>
                            <div className="text-xs text-text-secondary mt-0.5" data-id="element-1283">
                              {request.mediaTypes?.join(', ') || (request as any).mediaType}
                            </div>
                            {request.positionIds && request.positionIds.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-1284">
                                  {(() => {
                        const positionNames: string[] = [];
                        request.positionIds!.forEach((pid: string) => {
                          stores.forEach(store => {
                            const pos = store.positions?.find(p => p.id === pid);
                            if (pos && !positionNames.includes(pos.name)) {
                              positionNames.push(pos.name);
                            }
                          });
                        });
                        return positionNames.slice(0, 4).map(name => <span key={name} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-surface-warm border border-border text-text-secondary font-medium" data-id="element-1285">
                                          {name}
                                        </span>);
                      })()}
                                  {(() => {
                        const positionNames = new Set<string>();
                        request.positionIds!.forEach((pid: string) => {
                          stores.forEach(store => {
                            const pos = store.positions?.find(p => p.id === pid);
                            if (pos) positionNames.add(pos.name);
                          });
                        });
                        return positionNames.size > 4 ? <span className="text-[10px] text-text-tertiary" data-id="element-1286">
                                        +{positionNames.size - 4} more
                                      </span> : null;
                      })()}
                                </div>}
                            {request.campaignName && <div className="text-xs font-medium text-primary mt-1" data-id="element-1287">
                                {request.campaignName}
                              </div>}
                          </td>

                          {/* Period */}
                          <td className="px-5 py-4" data-id="element-1288">
                            <div className="text-sm text-text-primary" data-id="element-1289">
                              Week {request.weekNumber}
                              {request.endWeekNumber && request.endWeekNumber !== request.weekNumber ? ` – ${request.endWeekNumber}` : ''}
                            </div>
                            <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1290">
                              {formatDate(request.weekStartDate)} –{' '}
                              {formatDate(request.weekEndDate)}
                            </div>
                          </td>

                          {/* Date */}
                          <td className="px-5 py-4 text-sm text-text-secondary" data-id="element-1291">
                            {formatDate(request.createdAt)}
                          </td>

                          {/* Requested By */}
                          <td className="px-5 py-4" data-id="element-1292">
                            <Badge variant={isWoolworths ? 'flagship' : 'standard'} data-id="element-1293">
                              {isWoolworths ? 'Woolworths' : request.brandName}
                            </Badge>
                          </td>

                          {/* Status */}
                          <td className="px-5 py-4" data-id="element-1294">
                            <div className="flex flex-col gap-1" data-id="element-1295">
                              <div className="flex items-center gap-2" data-id="element-1296">
                                {getStatusIcon(request.status)}
                                <Badge variant={getStatusBadgeVariant(request.status)} data-id="element-1297">
                                  {getStatusLabel(request.status, request.requestedBy)}
                                </Badge>
                              </div>
                              {request.approvedBy && <div className="flex items-center gap-1 text-[10px] text-text-tertiary mt-0.5" data-id="element-1298">
                                  <UserIcon className="w-3 h-3" data-id="element-1299" />
                                  {request.approvedBy}
                                  {request.respondedAt && <span data-id="element-1300">
                                      · {formatDate(request.respondedAt)}
                                    </span>}
                                </div>}
                              {request.approvalConditions && <div className="mt-2 p-2 bg-requested/5 border border-requested/20 rounded text-xs text-text-secondary" data-id="element-1301">
                                  <span className="font-semibold text-requested" data-id="element-1302">
                                    Conditions:
                                  </span>{' '}
                                  {request.approvalConditions}
                                </div>}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-4" data-id="element-1303">
                            {request.status === 'draft' ? <div className="flex flex-col gap-2" data-id="element-1304">
                                <Button variant="primary" size="sm" onClick={() => handleSendToBrand(request)} className="flex items-center gap-1.5" data-id="element-1305">
                                  <SendIcon className="w-3.5 h-3.5" data-id="element-1306" />
                                  Send to Brand
                                </Button>
                                <div className="flex items-center gap-2" data-id="element-1307">
                                  <Button variant="secondary" size="sm" onClick={() => {
                          setEditingRequest(request);
                          setIsEditModalOpen(true);
                        }} className="flex items-center gap-1.5" data-id="element-1308">
                                    <PencilIcon className="w-3.5 h-3.5" data-id="element-1309" />
                                    Edit
                                  </Button>
                                  <Button variant="secondary" size="sm" onClick={() => handleRemove(request)} className="border-urgent/50 text-urgent hover:bg-urgent/5 flex items-center gap-1.5" aria-label={`Remove request for ${request.brandName}`} data-id="element-1310">
                                    <Trash2Icon className="w-3.5 h-3.5" data-id="element-1311" />
                                    Remove
                                  </Button>
                                </div>
                              </div> : request.status === 'pending' ? isWoolworths ? <div className="flex flex-col gap-2" data-id="element-1312">
                                  <div className="flex items-center gap-1.5 text-xs text-text-tertiary" data-id="element-1313">
                                    <ClockIcon className="w-3.5 h-3.5" data-id="element-1314" />
                                    Awaiting brand response
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-1315">
                                    <Button variant="secondary" size="sm" onClick={() => {
                          setEditingRequest(request);
                          setIsEditModalOpen(true);
                        }} className="flex items-center gap-1.5" data-id="element-1316">
                                      <PencilIcon className="w-3.5 h-3.5" data-id="element-1317" />
                                      Edit & Resend
                                    </Button>
                                    <Button variant="secondary" size="sm" onClick={() => handleRemove(request)} className="border-urgent/50 text-urgent hover:bg-urgent/5 flex items-center gap-1.5" data-id="element-1318">
                                      <Trash2Icon className="w-3.5 h-3.5" data-id="element-1319" />
                                      Withdraw
                                    </Button>
                                  </div>
                                </div> : user.role === 'admin' ? <div className="flex flex-col gap-2" data-id="element-1320">
                                  <div className="flex items-center gap-2" data-id="element-1321">
                                    <Button variant="primary" size="sm" onClick={() => handleApproveAsIs(request)} data-id="element-1322">
                                      Approve
                                    </Button>
                                    <Button variant="secondary" size="sm" onClick={() => handleReject(request)} className="border-urgent text-urgent hover:bg-urgent/5" data-id="element-1323">
                                      Reject
                                    </Button>
                                  </div>
                                  <Button variant="ghost" size="sm" onClick={() => {
                        setEditingRequest(request);
                        setIsEditModalOpen(true);
                      }} className="text-text-secondary hover:text-text-primary flex items-center gap-1.5 justify-start px-0" data-id="element-1324">
                                    <PencilIcon className="w-3.5 h-3.5" data-id="element-1325" />
                                    Edit their request
                                  </Button>
                                </div> : <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-sm font-medium" data-id="element-1326">
                                  <ClockIcon className="w-3.5 h-3.5" data-id="element-1327" />
                                  Awaiting WW Approval
                                </div> : request.status === 'brand-accepted' ? user.role === 'admin' ? <div className="flex flex-col gap-2" data-id="element-1328">
                                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-sm font-medium" data-id="element-1329">
                                    <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1330" />
                                    Brand confirmed — approve to finalise
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-1331">
                                    <Button variant="primary" size="sm" onClick={() => handleApproveAsIs(request)} data-id="element-1332">
                                      Approve
                                    </Button>
                                    <Button variant="secondary" size="sm" onClick={() => handleApprove(request)} data-id="element-1333">
                                      Approve w/ Conditions
                                    </Button>
                                    <Button variant="secondary" size="sm" onClick={() => handleReject(request)} className="border-urgent text-urgent hover:bg-urgent/5" data-id="element-1334">
                                      Reject
                                    </Button>
                                  </div>
                                </div> : <div className="flex flex-col gap-2" data-id="element-1335">
                                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-sm font-medium" data-id="element-1336">
                                    <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1337" />
                                    Brand confirmed — awaiting WW approval
                                  </div>
                                </div> : request.status === 'accepted' || request.status === 'accepted-with-conditions' || request.status === 'rejected' ? <div className="flex flex-col gap-2" data-id="element-1338">
                                {isWoolworths && <Button variant="secondary" size="sm" onClick={() => {
                        setEditingRequest(request);
                        setIsEditModalOpen(true);
                      }} className="flex items-center gap-1.5" data-id="element-1339">
                                    <PencilIcon className="w-3.5 h-3.5" data-id="element-1340" />
                                    Edit & Resend
                                  </Button>}
                                {!isWoolworths && <span className="text-xs text-text-tertiary" data-id="element-1341">
                                    —
                                  </span>}
                              </div> : <span className="text-xs text-text-tertiary" data-id="element-1342">
                                —
                              </span>}
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </main>
      </PageTransition>

      <ConfirmDialog isOpen={confirmDialog.isOpen} title={confirmDialog.title} description={confirmDialog.description} isDestructive={true} confirmText="Yes, proceed" onCancel={() => setConfirmDialog(prev => ({
      ...prev,
      isOpen: false
    }))} onConfirm={confirmDialog.action} data-id="element-1343" />

      {/* Approve Modal */}
      <ApproveModal isOpen={approveModal.isOpen} request={approveModal.request} onClose={() => setApproveModal({
      isOpen: false,
      request: null
    })} onApproveAsIs={handleApproveAsIs} onApproveWithConditions={handleApproveWithConditions} data-id="element-1344" />

      {/* Edit Request Modal */}
      <BrandRequestModal isOpen={isEditModalOpen} stores={stores} editingDraft={editingRequest} onClose={() => {
      setIsEditModalOpen(false);
      setEditingRequest(null);
    }} onSendRequest={onSendRequest} onUpdateRequest={onUpdateRequest} data-id="element-1345" />

      {/* Notification Toast */}
      {emailNotification && <div className={`
            fixed bottom-6 right-6 z-50
            max-w-sm w-full p-4 rounded-xl shadow-lg border
            ${emailNotification.action === 'approved' ? 'bg-white border-available/30' : emailNotification.action === 'sent' ? 'bg-white border-primary/30' : emailNotification.action === 'removed' ? 'bg-white border-border' : 'bg-white border-urgent/30'}
          `} style={{
      animation: 'slideUp 0.3s ease-out'
    }} data-id="element-1346">
          <div className="flex items-start gap-3" data-id="element-1347">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
              ${emailNotification.action === 'approved' ? 'bg-available/10' : emailNotification.action === 'sent' ? 'bg-primary/10' : emailNotification.action === 'removed' ? 'bg-surface-warm' : 'bg-urgent/10'}
            `} data-id="element-1348">
              {emailNotification.action === 'approved' ? <CheckCircleIcon className="w-5 h-5 text-available" data-id="element-1349" /> : emailNotification.action === 'sent' ? <SendIcon className="w-5 h-5 text-primary" data-id="element-1350" /> : emailNotification.action === 'removed' ? <Trash2Icon className="w-5 h-5 text-text-tertiary" data-id="element-1351" /> : <XCircleIcon className="w-5 h-5 text-urgent" data-id="element-1352" />}
            </div>
            <div className="flex-1 min-w-0" data-id="element-1353">
              <div className="text-sm font-medium text-text-primary" data-id="element-1354">
                {emailNotification.action === 'approved' ? 'Request Approved' : emailNotification.action === 'sent' ? 'Request Sent to Brand' : emailNotification.action === 'removed' ? 'Request Removed' : 'Request Rejected'}
              </div>
              {emailNotification.action === 'sent' && <div className="text-xs text-text-secondary mt-1" data-id="element-1355">
                  Request sent to {emailNotification.brandName} for approval.
                </div>}
              {emailNotification.action !== 'removed' && emailNotification.action !== 'sent' && <div className="flex items-center gap-1.5 mt-1" data-id="element-1356">
                    <MailIcon className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" data-id="element-1357" />
                    <span className="text-xs text-text-secondary truncate" data-id="element-1358">
                      Email sent to {emailNotification.brandName}
                    </span>
                  </div>}
              {emailNotification.action === 'removed' && <div className="text-xs text-text-secondary mt-1" data-id="element-1359">
                  Request to {emailNotification.brandName} has been withdrawn.
                </div>}
              <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-border/50" data-id="element-1360">
                <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wide" data-id="element-1361">
                  CC:
                </span>
                <span className="text-[10px] text-text-tertiary" data-id="element-1362">
                  hello@grapevine.co.za
                </span>
              </div>
            </div>
            <button onClick={() => setEmailNotification(null)} className="text-text-tertiary hover:text-text-primary p-0.5" data-id="element-1363">
              <XCircleIcon className="w-4 h-4" data-id="element-1364" />
            </button>
          </div>
        </div>}

      <style data-id="element-1365">{`
        @keyframes slideUp {
          from { transform: translateY(16px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>;
}